import { createContext, createRef, useCallback, useContext, useMemo, useState } from 'react';
import type { FC, PropsWithChildren, RefObject } from 'react';
import noop from 'lodash-es/noop';

interface ITurnToComponentDataContext {
    reviewsRef?: RefObject<HTMLDivElement> | null;
    onToggleWriteReviewModal: (isOpen: boolean) => void;
    isWriteReviewModalOpen: boolean;
}

const TurnToComponentDataContext = createContext<ITurnToComponentDataContext>({
    reviewsRef: null,
    onToggleWriteReviewModal: noop,
    isWriteReviewModalOpen: false,
});

export type TurnToComponentDataProviderProps = PropsWithChildren;

export const TurnToComponentDataProvider: FC<TurnToComponentDataProviderProps> = ({ children }) => {
    const [isWriteReviewModalOpen, setIsWriteReviewModalOpen] = useState<boolean>(false);
    const reviewsRef = createRef<HTMLDivElement>();
    const onToggleWriteReviewModal = useCallback((isOpen: boolean) => {
        setIsWriteReviewModalOpen(isOpen);
    }, []);

    const ctx: ITurnToComponentDataContext = useMemo(
        () => ({
            reviewsRef,
            onToggleWriteReviewModal,
            isWriteReviewModalOpen,
        }),
        [reviewsRef, onToggleWriteReviewModal, isWriteReviewModalOpen],
    );

    return (
        <TurnToComponentDataContext.Provider value={ctx}>
            {children}
        </TurnToComponentDataContext.Provider>
    );
};

TurnToComponentDataProvider.defaultProps = {};

export const useTurnToComponentData = (): ITurnToComponentDataContext =>
    useContext(TurnToComponentDataContext);
